import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  EventbriteBulkImportItemDto,
  EventbriteBulkImportResultDto,
  EventbriteLinkedIvyEventDto,
  EventbriteOrganizationEventDto,
  EventbriteVenueAddResultDto,
  VenueDto,
  VenueEventVisibility,
} from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { formatEventbriteStart } from '../../utils/datetimeInput'

/** What the admin chose for one Eventbrite event before importing it. */
interface ImportDraft {
  include: boolean
  venuePublicId: string | null
  venueName: string | null
  /** Ivy name; empty keeps Eventbrite's. */
  name: string
  /** Null follows the tab's "Import as". */
  visibility: VenueEventVisibility | null
}

const VISIBILITY_LABELS: Record<VenueEventVisibility, string> = {
  [VenueEventVisibility.InternalOnly]: 'Internal only',
  [VenueEventVisibility.Public]: 'Public',
}

const MATCH_LABELS: Record<'name' | 'address', string> = { name: 'same name', address: 'same address' }

const ADDED_FROM_LABELS: Record<EventbriteVenueAddResultDto['source'], string> = {
  existing: 'Found on Ivy',
  google: 'Added to Ivy from Google',
  eventbrite: 'Added to Ivy from Eventbrite details',
}

/** Only a venue that is the same place (same name or street address) is picked and ticked by itself. */
const initialDraft = (event: EventbriteOrganizationEventDto): ImportDraft => {
  const suggestion = event.venueSuggestions.find(venue => venue.match)
  return {
    include: !event.linkedIvyEvents.length && !!suggestion,
    venuePublicId: suggestion?.venuePublicId ?? null,
    venueName: suggestion?.venueName ?? null,
    name: '',
    visibility: null,
  }
}

/**
 * Admin > Eventbrite: every upcoming event of Ivy's Eventbrite organization. Tick the ones to
 * bring into Ivy, adjust name / venue / visibility with Edit, then import. Events already on
 * Ivy are marked imported, link to their venue and can be removed from Ivy again.
 */
const EventbriteEventsTab = () => {
  const navigate = useNavigate()
  const [events, setEvents] = useState<EventbriteOrganizationEventDto[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, ImportDraft>>({})
  const [defaultVisibility, setDefaultVisibility] = useState<VenueEventVisibility>(VenueEventVisibility.InternalOnly)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [results, setResults] = useState<Record<string, EventbriteBulkImportResultDto>>({})
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<Record<string, string>>({})
  const [addingVenueId, setAddingVenueId] = useState<string | null>(null)
  const [venueNotes, setVenueNotes] = useState<Record<string, { text: string; isError: boolean }>>({})

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const loaded = await adminService.listEventbriteEvents()
      setEvents(loaded)
      // Keep the admin's choices across reloads; events that just landed on Ivy drop out.
      setDrafts(prev => {
        const next: Record<string, ImportDraft> = {}
        for (const event of loaded) {
          const kept = prev[event.eventbriteId]
          next[event.eventbriteId] = kept
            ? { ...kept, include: kept.include && !event.linkedIvyEvents.length }
            : initialDraft(event)
        }
        return next
      })
    } catch (err: any) {
      setEvents([])
      setLoadError(err?.friendlyMessage || err?.message || 'Could not load Eventbrite events.')
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const updateDraft = (eventbriteId: string, patch: Partial<ImportDraft>) =>
    setDrafts(prev => ({ ...prev, [eventbriteId]: { ...prev[eventbriteId], ...patch } }))

  const importable = useMemo(() => (events ?? []).filter(event => !event.linkedIvyEvents.length), [events])

  const selectedItems = useMemo<EventbriteBulkImportItemDto[]>(
    () =>
      importable
        .filter(event => drafts[event.eventbriteId]?.include && drafts[event.eventbriteId]?.venuePublicId)
        .map(event => {
          const draft = drafts[event.eventbriteId]
          return {
            eventbriteId: event.eventbriteId,
            venuePublicId: draft.venuePublicId!,
            name: draft.name.trim() || undefined,
            visibility: draft.visibility ?? defaultVisibility,
          }
        }),
    [importable, drafts, defaultVisibility],
  )

  const allSelected = importable.length > 0 && importable.every(event => drafts[event.eventbriteId]?.include)

  const toggleAll = (include: boolean) =>
    setDrafts(prev => {
      const next = { ...prev }
      for (const event of importable) next[event.eventbriteId] = { ...next[event.eventbriteId], include }
      return next
    })

  const handleImport = async () => {
    if (!selectedItems.length) return
    const missingVenue = importable.filter(
      event => drafts[event.eventbriteId]?.include && !drafts[event.eventbriteId]?.venuePublicId,
    ).length
    const message = [
      `Create ${selectedItems.length} Ivy event${selectedItems.length === 1 ? '' : 's'}?`,
      missingVenue ? `${missingVenue} ticked event${missingVenue === 1 ? ' has' : 's have'} no venue and will be left out.` : '',
    ]
      .filter(Boolean)
      .join('\n')
    if (!window.confirm(message)) return

    setIsImporting(true)
    try {
      const imported = await adminService.importEventbriteEventsToVenues(selectedItems, defaultVisibility)
      setResults(prev => ({ ...prev, ...Object.fromEntries(imported.map(result => [result.eventbriteId, result])) }))
      setEditingId(null)
      await load()
    } catch (err: any) {
      const error = err?.friendlyMessage || err?.message || 'Import failed.'
      setResults(prev => ({
        ...prev,
        ...Object.fromEntries(
          selectedItems.map(item => [
            item.eventbriteId,
            { eventbriteId: item.eventbriteId, venuePublicId: item.venuePublicId, status: 'failed', specialPublicId: null, error },
          ]),
        ),
      }))
    } finally {
      setIsImporting(false)
    }
  }

  /** Finds the event's Eventbrite venue on Ivy (or adds it from Google / Eventbrite) and picks it. */
  const handleAddVenue = async (event: EventbriteOrganizationEventDto) => {
    setAddingVenueId(event.eventbriteId)
    setVenueNotes(prev => ({ ...prev, [event.eventbriteId]: { text: '', isError: false } }))
    try {
      const added = await adminService.addEventbriteEventVenue(event.eventbriteId)
      updateDraft(event.eventbriteId, { venuePublicId: added.venuePublicId, venueName: added.venueName, include: true })
      setVenueNotes(prev => ({ ...prev, [event.eventbriteId]: { text: ADDED_FROM_LABELS[added.source], isError: false } }))
    } catch (err: any) {
      setVenueNotes(prev => ({
        ...prev,
        [event.eventbriteId]: { text: err?.friendlyMessage || err?.message || 'Could not add the venue.', isError: true },
      }))
    } finally {
      setAddingVenueId(null)
    }
  }

  /** Deletes the Ivy copy only; the event stays on Eventbrite and can be imported again. */
  const handleRemove = async (event: EventbriteOrganizationEventDto, linked: EventbriteLinkedIvyEventDto) => {
    const name = event.name || 'this event'
    if (!window.confirm(`Remove "${name}" from ${linked.venueName} on Ivy?\nIt stays on Eventbrite. Its Ivy RSVPs, comments and likes are deleted.`)) return
    setRemovingId(linked.specialPublicId)
    setRemoveError(prev => ({ ...prev, [event.eventbriteId]: '' }))
    try {
      await adminService.deleteVenueSpecial(linked.venuePublicId, linked.specialPublicId)
      await load()
    } catch (err: any) {
      setRemoveError(prev => ({
        ...prev,
        [event.eventbriteId]: err?.friendlyMessage || err?.message || 'Could not remove the Ivy event.',
      }))
    } finally {
      setRemovingId(null)
    }
  }

  const importedCount = (events ?? []).length - importable.length

  if (events === null) {
    return <p className="eventbrite-events-tab__note">Loading Eventbrite events…</p>
  }

  return (
    <div className="eventbrite-events-tab">
      <div className="eventbrite-events-tab__toolbar">
        <p className="eventbrite-events-tab__note">
          {events.length} upcoming event{events.length === 1 ? '' : 's'} of Ivy's Eventbrite organization, private ones and
          drafts included ({importedCount} already imported). Tick the ones to bring into Ivy, use Edit to change the name,
          venue or visibility, then import. Times are each event's local time.
        </p>
        <label>
          Import as
          <select value={defaultVisibility} onChange={e => setDefaultVisibility(e.target.value as VenueEventVisibility)}>
            {Object.values(VenueEventVisibility).map(value => (
              <option key={value} value={value}>
                {VISIBILITY_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="eb-button" onClick={load} disabled={isImporting || !!removingId}>
          Refresh
        </button>
        <button type="button" className="eb-button eb-button--primary" disabled={!selectedItems.length || isImporting} onClick={handleImport}>
          {isImporting ? 'Importing…' : `Import selected (${selectedItems.length})`}
        </button>
      </div>

      {loadError && <p className="eventbrite-events-tab__error">{loadError}</p>}
      {!loadError && !events.length && <p className="eventbrite-events-tab__note">No upcoming Eventbrite events.</p>}

      {events.length > 0 && (
        <table className="eventbrite-events-table">
          <thead>
            <tr>
              <th className="eventbrite-events-table__check">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  disabled={!importable.length || isImporting}
                  onChange={e => toggleAll(e.target.checked)}
                />
              </th>
              <th>Event</th>
              <th>Starts</th>
              <th>Eventbrite venue</th>
              <th>Ivy venue</th>
              <th>Visibility</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {events.map(event => {
              const draft = drafts[event.eventbriteId] ?? initialDraft(event)
              const result = results[event.eventbriteId]
              const isLinked = event.linkedIvyEvents.length > 0
              const isEditing = editingId === event.eventbriteId
              const tags = [event.status === 'draft' ? 'Draft' : null, !event.listed ? 'Private' : null].filter(Boolean)
              return (
                <Fragment key={event.eventbriteId}>
                  <tr className={isLinked ? 'eventbrite-events-table__row--linked' : undefined}>
                    <td className="eventbrite-events-table__check">
                      {!isLinked && (
                        <input
                          type="checkbox"
                          aria-label={`Include ${event.name}`}
                          checked={draft.include}
                          disabled={isImporting}
                          onChange={e => updateDraft(event.eventbriteId, { include: e.target.checked })}
                        />
                      )}
                    </td>
                    <td>
                      <span className="eventbrite-events-table__name">{draft.name.trim() || event.name || 'Untitled event'}</span>
                      {isLinked && <span className="eventbrite-events-table__badge">Already imported</span>}
                      {draft.name.trim() && draft.name.trim() !== event.name && (
                        <span className="eventbrite-events-table__sub">On Eventbrite: {event.name}</span>
                      )}
                      <span className="eventbrite-events-table__tags">
                        <a className="eventbrite-events-table__eb-link" href={event.url} target="_blank" rel="noreferrer">
                          <img src="/images/eventbrite.png" alt="" />
                          Eventbrite page
                        </a>
                        {tags.length > 0 && ` · ${tags.join(' · ')}`}
                      </span>
                    </td>
                    <td>{formatEventbriteStart(event.eventStartDate, event.timezone)}</td>
                    <td>
                      {event.isOnline ? 'Online' : event.venueName || '—'}
                      {!event.isOnline && event.address && <span className="eventbrite-events-table__sub">{event.address}</span>}
                    </td>
                    <td>
                      {isLinked ? (
                        event.linkedIvyEvents.map(linked => (
                          <div key={linked.specialPublicId} className="eventbrite-events-table__linked">
                            <button
                              type="button"
                              className="eventbrite-events-table__link"
                              onClick={() => navigate(`/admin/venues/${linked.venuePublicId}`)}
                            >
                              On Ivy at {linked.venueName}
                            </button>
                            <button
                              type="button"
                              className="eb-button eb-button--danger"
                              disabled={!!removingId || isImporting}
                              onClick={() => handleRemove(event, linked)}
                            >
                              {removingId === linked.specialPublicId ? 'Removing…' : 'Remove'}
                            </button>
                          </div>
                        ))
                      ) : draft.venueName ? (
                        draft.venueName
                      ) : (
                        <div className="eventbrite-events-table__no-venue">
                          <span className="eventbrite-events-tab__error">No matching Ivy venue</span>
                          {!event.isOnline && event.venueName && event.address && (
                            <button
                              type="button"
                              className="eb-button"
                              disabled={!!addingVenueId || isImporting}
                              onClick={() => handleAddVenue(event)}
                            >
                              {addingVenueId === event.eventbriteId ? 'Adding…' : `Add ${event.venueName} to Ivy`}
                            </button>
                          )}
                        </div>
                      )}
                      {!isLinked && venueNotes[event.eventbriteId]?.text && (
                        <span
                          className={
                            venueNotes[event.eventbriteId].isError ? 'eventbrite-events-tab__error' : 'eventbrite-events-table__sub'
                          }
                        >
                          {venueNotes[event.eventbriteId].text}
                        </span>
                      )}
                      {result?.status === 'failed' && <span className="eventbrite-events-tab__error">{result.error}</span>}
                      {removeError[event.eventbriteId] && (
                        <span className="eventbrite-events-tab__error">{removeError[event.eventbriteId]}</span>
                      )}
                    </td>
                    <td>{isLinked ? '—' : VISIBILITY_LABELS[draft.visibility ?? defaultVisibility]}</td>
                    <td>
                      {!isLinked && (
                        <button
                          type="button"
                          className="eb-button"
                          disabled={isImporting}
                          onClick={() => setEditingId(isEditing ? null : event.eventbriteId)}
                        >
                          {isEditing ? 'Done' : 'Edit'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isEditing && (
                    <tr className="eventbrite-events-table__edit-row">
                      <td />
                      <td colSpan={6}>
                        <QuickEdit
                          event={event}
                          draft={draft}
                          defaultVisibility={defaultVisibility}
                          onChange={patch => updateDraft(event.eventbriteId, { ...patch, include: true })}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

interface QuickEditProps {
  event: EventbriteOrganizationEventDto
  draft: ImportDraft
  defaultVisibility: VenueEventVisibility
  onChange: (patch: Partial<ImportDraft>) => void
}

/** Name, venue (nearby suggestions or a search over all venues) and visibility for one event. */
const QuickEdit = ({ event, draft, defaultVisibility, onChange }: QuickEditProps) => {
  const [venueQuery, setVenueQuery] = useState('')
  const [venueResults, setVenueResults] = useState<VenueDto[]>([])

  useEffect(() => {
    const query = venueQuery.trim()
    if (query.length < 2) {
      setVenueResults([])
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      adminService
        .getVenues(1, 8, query)
        .then(response => {
          if (!cancelled) setVenueResults(response.venues)
        })
        .catch(() => {
          if (!cancelled) setVenueResults([])
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [venueQuery])

  const pickVenue = (venuePublicId: string | null, venueName: string | null) => {
    onChange({ venuePublicId, venueName })
    setVenueQuery('')
    setVenueResults([])
  }

  return (
    <div className="eventbrite-quick-edit">
      <label>
        Name on Ivy
        <input
          type="text"
          value={draft.name}
          placeholder={event.name}
          maxLength={200}
          onChange={e => onChange({ name: e.target.value })}
        />
      </label>

      <label>
        Ivy venue
        <select
          value={draft.venuePublicId ?? ''}
          onChange={e => {
            const suggestion = event.venueSuggestions.find(venue => venue.venuePublicId === e.target.value)
            if (suggestion) pickVenue(suggestion.venuePublicId, suggestion.venueName)
            else if (!e.target.value) pickVenue(null, null)
          }}
        >
          <option value="">No venue</option>
          {event.venueSuggestions.map(venue => (
            <option key={venue.venuePublicId} value={venue.venuePublicId}>
              {venue.venueName} ({[venue.match ? MATCH_LABELS[venue.match] : null, `${venue.distanceMeters} m away`]
                .filter(Boolean)
                .join(' · ')})
            </option>
          ))}
          {draft.venuePublicId && !event.venueSuggestions.some(venue => venue.venuePublicId === draft.venuePublicId) && (
            <option value={draft.venuePublicId}>{draft.venueName}</option>
          )}
        </select>
        <input
          type="text"
          value={venueQuery}
          placeholder="Or search all venues…"
          onChange={e => setVenueQuery(e.target.value)}
        />
        {venueResults.length > 0 && (
          <ul className="eventbrite-quick-edit__results">
            {venueResults.map(venue => (
              <li key={venue.publicId}>
                <button type="button" onClick={() => pickVenue(venue.publicId, venue.name)}>
                  {venue.name}
                  {venue.address && <span>{venue.address}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </label>

      <label>
        Visibility
        <select
          value={draft.visibility ?? ''}
          onChange={e => onChange({ visibility: (e.target.value || null) as VenueEventVisibility | null })}
        >
          <option value="">Same as Import as ({VISIBILITY_LABELS[defaultVisibility]})</option>
          {Object.values(VenueEventVisibility).map(value => (
            <option key={value} value={value}>
              {VISIBILITY_LABELS[value]}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}

export default EventbriteEventsTab
